/**
 * ArrowPropertiesPanel — Phase 5
 * Floating panel that appears when an arrow is selected,
 * allowing users to edit connection style, label, and type.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useArrowStore, Arrow, ArrowStyle, ConnectionType } from '@/stores/arrowStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { getConnectorPoint } from './ArrowPath';
import { Trash2 } from 'lucide-react';

const STYLE_OPTIONS: { value: ArrowStyle; label: string }[] = [
  { value: 'bezier', label: 'Curved' },
  { value: 'straight', label: 'Straight' },
  { value: 'step', label: 'Right-angle' },
];

const TYPE_OPTIONS: { value: ConnectionType; label: string; color: string }[] = [
  { value: 'default', label: 'Default', color: '#94a3b8' },
  { value: 'causes', label: 'Causes', color: '#f97316' },
  { value: 'blocks', label: 'Blocks', color: '#ef4444' },
  { value: 'contains', label: 'Contains', color: '#8b5cf6' },
  { value: 'sequence', label: 'Sequence', color: '#3b82f6' },
  { value: 'related', label: 'Related', color: '#6b7280' },
];

export function ArrowPropertiesPanel() {
  const { selectedArrowId, arrows, updateArrow, deleteArrow, setSelectedArrow } = useArrowStore();
  const { items, panX, panY, zoom } = useCanvasStore();
  const [labelValue, setLabelValue] = useState('');

  const arrow = selectedArrowId ? arrows.find(a => a.id === selectedArrowId) : null;

  useEffect(() => {
    if (arrow) {
      setLabelValue(arrow.label || '');
    }
  }, [arrow?.id, arrow?.label]);

  const handleLabelBlur = useCallback(() => {
    if (!arrow) return;
    const newLabel = labelValue.trim() || null;
    if (newLabel !== (arrow.label || null)) {
      updateArrow(arrow.id, { label: newLabel });
    }
  }, [arrow, labelValue, updateArrow]);

  const handleLabelKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
    // Prevent global keyboard shortcuts from firing
    e.stopPropagation();
  }, []);

  if (!arrow) return null;

  // Compute screen position for the panel (at midpoint of the arrow)
  const startItem = arrow.start_item_id ? items.find(i => i.id === arrow.start_item_id) : null;
  const endItem = arrow.end_item_id ? items.find(i => i.id === arrow.end_item_id) : null;

  let midX = 0;
  let midY = 0;

  const startPt = startItem && arrow.start_connector
    ? getConnectorPoint(startItem, arrow.start_connector)
    : arrow.start_x !== null && arrow.start_y !== null
      ? { x: arrow.start_x, y: arrow.start_y }
      : null;

  const endPt = endItem && arrow.end_connector
    ? getConnectorPoint(endItem, arrow.end_connector)
    : arrow.end_x !== null && arrow.end_y !== null
      ? { x: arrow.end_x, y: arrow.end_y }
      : null;

  if (startPt && endPt) {
    midX = ((startPt.x + endPt.x) / 2) * zoom + panX;
    midY = ((startPt.y + endPt.y) / 2) * zoom + panY;
  }

  // Clamp to viewport so panel doesn't go off-screen
  const panelWidth = 220;
  const panelHeight = 200;
  const clampedX = Math.max(8, Math.min(midX - panelWidth / 2, window.innerWidth - panelWidth - 8));
  const clampedY = Math.max(8, Math.min(midY - panelHeight - 20, window.innerHeight - panelHeight - 8));

  return (
    <div
      className="fixed z-50 bg-card border rounded-lg shadow-lg p-3 w-[220px]"
      style={{ left: clampedX, top: clampedY }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {/* Label input */}
      <div className="mb-2">
        <label className="text-xs text-muted-foreground mb-1 block">Label</label>
        <input
          type="text"
          value={labelValue}
          onChange={e => setLabelValue(e.target.value)}
          onBlur={handleLabelBlur}
          onKeyDown={handleLabelKeyDown}
          placeholder="Add label..."
          className="w-full text-sm bg-background border rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Style selector */}
      <div className="mb-2">
        <label className="text-xs text-muted-foreground mb-1 block">Style</label>
        <div className="flex gap-1">
          {STYLE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => updateArrow(arrow.id, { style: opt.value })}
              className={`flex-1 text-xs px-2 py-1 rounded transition-colors ${
                (arrow.style || 'bezier') === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Connection type selector */}
      <div className="mb-2">
        <label className="text-xs text-muted-foreground mb-1 block">Type</label>
        <div className="grid grid-cols-3 gap-1">
          {TYPE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => updateArrow(arrow.id, {
                connection_type: opt.value,
                stroke_color: opt.color,
              })}
              className={`text-xs px-1.5 py-1 rounded transition-colors flex items-center gap-1 ${
                (arrow.connection_type || 'default') === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: opt.color }}
              />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Animated toggle + Delete */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t">
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={arrow.animated ?? false}
            onChange={e => updateArrow(arrow.id, { animated: e.target.checked })}
            className="rounded"
          />
          Animated
        </label>
        <button
          onClick={() => {
            deleteArrow(arrow.id);
            setSelectedArrow(null);
          }}
          className="text-xs text-destructive hover:bg-destructive hover:text-white px-2 py-1 rounded transition-colors flex items-center gap-1"
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </div>
  );
}
